import Behavior from 'dashboard/components/GraphCanvasV2/behaviors/behavior';
import CanvasMouseUpEvent from 'dashboard/components/GraphCanvasV2/events/canvas-mouse-up-event';
import CanvasViewportChangeEvent from 'dashboard/components/GraphCanvasV2/events/canvas-viewport-change-event';
import CanvasWheelEvent from 'dashboard/components/GraphCanvasV2/events/canvas-wheel-event';
import KeyDownEvent from 'dashboard/components/GraphCanvasV2/events/key-down-event';
import KeyUpEvent from 'dashboard/components/GraphCanvasV2/events/key-up-event';

const ZOOM_SENSITIVITY = 1.5;
const DELTA = 0.05;
export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 2;

export default class CameraSelectBehavior extends Behavior {
  private zPressed = false;
  private altPressed = false;
  private ctrlPressed = false;
  private metaPressed = false;

  public get events() {
    return {
      [CanvasWheelEvent.type]: 'handleWheel',
      [KeyDownEvent.type]: 'handleKeyDown',
      [KeyUpEvent.type]: 'handleKeyUp',
      [CanvasMouseUpEvent.type]: 'handleCanvasMouseUp',
    };
  }

  public reactivate() {
    this.processSideEffects();
  }

  public reset() {
    this.zPressed = false;
    this.altPressed = false;
    this.ctrlPressed = false;
    this.metaPressed = false;
  }

  public handleWheel(e: CanvasWheelEvent) {
    const canvas = this.g6Wrapper.canvas;
    // Browsers do a weird thing where they set ctrlKey to true
    // in the event this is a pinch-to-zoom rather than a pan.
    if (e.raw.ctrlKey || this.ctrlPressed || this.metaPressed) {
      const point = canvas.getPointByClient(e.raw.x, e.raw.y);
      const graphZoom = this.g6Wrapper.graph.getZoom();

      let ratio = graphZoom;
      let zoom = graphZoom;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - incorrect typescript definition
      if (e.raw.wheelDelta < 0) {
        ratio = 1 - DELTA * ZOOM_SENSITIVITY;
      } else {
        ratio = 1 / (1 - DELTA * ZOOM_SENSITIVITY);
      }
      zoom = graphZoom * ratio;

      if (zoom > MAX_ZOOM || zoom < MIN_ZOOM) {
        return;
      }

      this.g6Wrapper.graph.zoomTo(zoom, { x: point.x, y: point.y });
      this.eventBus.dispatchEvent(new CanvasViewportChangeEvent(e, canvas));
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - incorrect typescript definition
      this.g6Wrapper.graph.translate(e.raw.wheelDeltaX * 0.3, e.raw.wheelDeltaY * 0.3);
      this.eventBus.dispatchEvent(new CanvasViewportChangeEvent(e, canvas));
    }
  }

  public handleCanvasMouseUp(e: CanvasMouseUpEvent) {
    if (this.zPressed && this.altPressed) {
      const point = this.g6Wrapper.canvas.getPointByClient(e.raw.x, e.raw.y);
      const graphZoom = this.g6Wrapper.graph.getZoom();
      const zoom = graphZoom * 0.8;

      this.g6Wrapper.graph.zoomTo(zoom, { x: point.x, y: point.y });
    } else if (this.zPressed) {
      const point = this.g6Wrapper.canvas.getPointByClient(e.raw.x, e.raw.y);
      const graphZoom = this.g6Wrapper.graph.getZoom();
      const zoom = graphZoom * 1.2;

      this.g6Wrapper.graph.zoomTo(zoom, { x: point.x, y: point.y });
    }
  }

  public handleKeyDown(e: KeyDownEvent) {
    if (e.raw.code === 'KeyZ') {
      this.zPressed = true;
    } else if (e.raw.key === 'Control') {
      this.ctrlPressed = true;
    } else if (e.raw.key === 'Meta') {
      this.metaPressed = true;
    } else if (e.raw.key === 'Alt') {
      this.altPressed = true;
    }

    this.processSideEffects();
  }

  public handleKeyUp(e: KeyUpEvent) {
    if (e.raw.code === 'KeyZ') {
      this.zPressed = false;
    } else if (e.raw.key === 'Control') {
      this.ctrlPressed = false;
    } else if (e.raw.key === 'Meta') {
      this.metaPressed = false;
    } else if (e.raw.key === 'Alt') {
      this.altPressed = false;
    }

    this.processSideEffects();
  }

  private processSideEffects() {
    const { canvasEl } = this.g6Wrapper;
    const styles = canvasEl
      .getAttribute('style')
      .split(';')
      .filter(Boolean)
      .reduce((styleHash: Record<string, unknown>, kv: string) => {
        const [key, value] = kv.split(':');
        return {
          ...styleHash,
          [key.trim()]: value.trim(),
        };
      }, {});

    let changed = false;
    if (this.zPressed && this.altPressed) {
      if (styles['cursor'] !== 'zoom-out') {
        styles['cursor'] = 'zoom-out';
        changed = true;
      }
    } else if (this.zPressed) {
      if (styles['cursor'] !== 'zoom-in') {
        styles['cursor'] = 'zoom-in';
        changed = true;
      }
    } else {
      if (styles['cursor'] !== 'cursor') {
        styles['cursor'] = 'cursor';
        changed = true;
      }
    }

    if (changed) {
      const styleString = Object.keys(styles).reduce((string, styleKey) => {
        return `${string}${styleKey}: ${styles[styleKey]};`;
      }, '');
      canvasEl.setAttribute('style', styleString);
    }
  }
}
